import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Box, Button, Card, Typography } from "@mui/material";
import { useMemo } from "react";
import { AppColors } from "../../../../../util/AppColors";
import {
  getWorkbookItemActualCost,
  getWorkbookItemDisplayCost,
} from "../workbookInventoryHelpers";

const ConsumableCard = ({
  consumable,
  onEdit,
  onDelete,
  onToggleUsed,
  isToggling = false,
}) => {
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicId, locations } = useMemo(() => {
    if (!mandateDataRaw)
      return { clinicStocks: [], clinicId: null, locations: [] };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        clinicId: reqData?.clinic?.id ?? null,
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return { clinicStocks: [], clinicId: null, locations: [] };
    }
  }, [mandateDataRaw]);

  const reqStock = useMemo(() => {
    if (!consumable?.stockId) return null;
    return (
      clinicStocks?.find((stc) => stc?.stockId === consumable?.stockId) ?? null
    );
  }, [consumable?.stockId, clinicStocks]);

  const batchNo = useMemo(() => {
    if (consumable?.batchNo) return consumable.batchNo;
    if (!consumable?.batchId || !reqStock) return null;

    const reqBatch = reqStock?.stockBatch
      ?.filter((btch) => btch?.createdBy === clinicId)
      ?.find((bat) => bat?.batchId === consumable?.batchId);

    return reqBatch?.batchNo ?? null;
  }, [consumable, reqStock, clinicId]);

  const isControlledSubstance = useMemo(() => {
    if (!reqStock) return false;
    const isScheduleH1 =
      (reqStock?.drugSchedule ?? "")?.toLowerCase() === "schedule h1";
    const isNdps =
      reqStock?.ndps === true ||
      reqStock?.ndps === "Y" ||
      reqStock?.ndps === "y";
    return isScheduleH1 || isNdps;
  }, [reqStock]);

  const isRequested = consumable?.status === "requested";

  const isRejected =
    consumable?.status === "rejected" ||
    consumable?.isRejected ||
    consumable?.remarks === "Rejected";

  const isStockUnavailable =
    consumable?.status === "rejected" &&
    consumable?.remarks === "Stock Unavailable";

  const isUsed =
    !isRequested &&
    !isRejected &&
    (consumable?.status === true ||
      consumable?.status === "true" ||
      consumable?.remarks === "Completed");

  const getDotColor = (status) => {
    if (status === "green") return "#4caf50";
    if (status === "yellow") return "#ff9800";
    if (status === "red") return "#f44336";
    if (isRequested) return "#ff9800";
    if (isStockUnavailable) return "#ff9800";
    if (isRejected) return "#f44336";
    if (isUsed) return "#4caf50";
    return "#4caf50";
  };

  const selectedBatch = useMemo(() => {
    if (!reqStock) return null;
    const batchId =
      consumable?.batchId ?? consumable?.batchNumber?.value ?? null;
    if (batchId) {
      return (
        reqStock?.stockBatch?.find(
          (bat) => String(bat?.batchId) === String(batchId),
        ) ?? null
      );
    }
    return (
      reqStock?.stockBatch?.find((btch) => btch?.createdBy === clinicId) ??
      reqStock?.stockBatch?.[0] ??
      null
    );
  }, [reqStock, consumable?.batchId, consumable?.batchNumber?.value, clinicId]);

  const locationName = useMemo(() => {
    if (consumable?.batchNumber?.locationName) {
      return consumable.batchNumber.locationName;
    }
    if (!selectedBatch?.locationId) return null;
    return (
      locations?.find((loc) => loc?.id === selectedBatch?.locationId)
        ?.locationName ?? null
    );
  }, [consumable, selectedBatch, locations]);

  const quantity = Number(consumable?.quantity ?? 0);
  const uomName =
    consumable?.uomName ||
    selectedBatch?.salesUom ||
    selectedBatch?.qtyMapName ||
    selectedBatch?.uomMapName ||
    "";
  const actualCost = useMemo(
    () =>
      getWorkbookItemActualCost({
        item: consumable,
        selectedBatch,
        quantity,
      }),
    [consumable, selectedBatch, quantity],
  );
  const displayCost = getWorkbookItemDisplayCost(consumable, actualCost);

  return (
    <Card
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.06)",
        border: "1px solid #e0e0e0",
        p: 2,
        mb: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      {/* Top Main Row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        {/* Left: Dot & Name */}
        <Box display="flex" alignItems="center" gap={1.2}>
          <Box
            sx={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              backgroundColor: getDotColor(consumable?.status),
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#212121",
            }}
          >
            {consumable?.procedureName}
          </Typography>
        </Box>

        {/* Right: Quantity/Price, Requested Badge, Edit & Delete */}
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#616161",
              fontWeight: 500,
            }}
          >
            {quantity} {uomName} · ₹{Number(displayCost).toFixed(2)}
          </Typography>

          {isRequested ? (
            <Box
              sx={{
                backgroundColor: "#fff3e0",
                color: "#e65100",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.2,
                py: 0.3,
                borderRadius: "4px",
                border: "1px solid #ffe082",
              }}
            >
              Requested
            </Box>
          ) : isRejected ? (
            <Typography
              className={`${
                isStockUnavailable ? "orange-back2" : "red-back2"
              } white-color status-con fs12 font-medium con-fit`}
            >
              {isStockUnavailable ? "Stock Unavailable" : "Rejected"}
            </Typography>
          ) : (
            <>
              <Box
                sx={{
                  backgroundColor: isUsed ? "#e8f5e9" : "#e0f2fe",
                  color: isUsed ? "#2e7d32" : "#0284c7",
                  fontSize: "12px",
                  fontWeight: 600,
                  px: 1.2,
                  py: 0.3,
                  borderRadius: "4px",
                  border: isUsed ? "1px solid #c8e6c9" : "1px solid #bae6fd",
                }}
              >
                {isUsed ? "Used" : "Issued"}
              </Box>

              {!isUsed && (
                <Button
                  size="small"
                  variant="contained"
                  disabled={isToggling}
                  onClick={() => onToggleUsed && onToggleUsed(consumable)}
                  sx={{
                    backgroundColor: AppColors.appPrimary,
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "12px",
                    height: "30px",
                    px: 1.5,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#002b58",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: AppColors.appPrimary,
                      color: "#fff",
                      opacity: 0.6,
                    },
                  }}
                >
                  Mark Consumed
                </Button>
              )}
            </>
          )}

          <Button
            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            onClick={() => onEdit(consumable)}
            sx={{
              color: AppColors.appPrimary,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              minWidth: "auto",
              p: "4px 8px",
              whiteSpace: "nowrap",
            }}
          >
            Edit
          </Button>
          {consumable?.billing &&
          ["true", "issued"].includes(
            (consumable?.status ?? "")?.toLowerCase(),
          ) ? null : (
            <Button
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={() => onDelete(consumable?.detailId ?? consumable?.id)}
              sx={{
                color: "#d32f2f",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                minWidth: "auto",
                p: "4px 8px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>
      {/* Sub-row: Badges (Batch, Package Inclusion / Billed Separately / Pharmacy, Procedure Tag) */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        {batchNo ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Batch: {batchNo}
          </Box>
        ) : null}

        {locationName ? (
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#616161",
              fontSize: "11px",
              fontWeight: 500,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Location: {locationName}
          </Box>
        ) : null}

        {consumable?.billSeparated ? (
          <Box
            sx={{
              backgroundColor: "#fff8e1",
              color: "#e65100",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #ffe082",
            }}
          >
            {`+ ₹${Number(actualCost).toFixed(2)} billed separately (Clinic)`}
          </Box>
        ) : null}

        {consumable?.billing && (
          <Box
            sx={{
              backgroundColor: "#e8eaf6",
              color: "#283593",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.4,
              borderRadius: "4px",
              border: "1px solid #c5cae9",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <span>📦</span> Requested from Pharmacy
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: consumable?.linked === "Y" ? "#f5f5f5" : "#e3f2fd",
            color:
              consumable?.linked === "Y" ? "#616161" : AppColors.appPrimary,
            fontSize: "12px",
            fontWeight: 600,
            px: 1.2,
            py: 0.4,
            borderRadius: consumable?.linked === "Y" ? "4px" : "12px",
            border: consumable?.linked === "Y" ? "1px solid #e0e0e0" : "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {consumable?.linked !== "Y" && (
            <LocalOfferIcon sx={{ fontSize: 13 }} />
          )}
          {consumable?.linked !== "Y"
            ? "Not linked"
            : consumable?.procedure?.procedureName}
        </Box>
        {isControlledSubstance ? (
          <Box
            sx={{
              backgroundColor: "#ffebee",
              color: "#d32f2f",
              fontSize: "12px",
              fontWeight: 600,
              px: 1.2,
              py: 0.3,
              borderRadius: "4px",
              border: "1px solid #ffcdd2",
            }}
          >
            Controlled substance
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export default ConsumableCard;
